import React, { useState, useEffect } from "react";
import {
	Dropdown,
	Button,
	Checkbox,
	Input,
	Space,
	Tooltip,
	Popover,
	Typography,
} from "antd";
import type { CheckboxOptionType } from "antd/es/checkbox/Group";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { GetAntIcon } from "../utils/ant_icons";
import { SearchOutlined } from "@ant-design/icons";

const CheckboxDropdown = ({
	options,
	onChange,
	onClear,
	defaultValue,
	Key,
	label,
	icon,
	page,
	hideArrow,
}: {
	options: any;
	onChange: (selectedValues: string[]) => void;
	onClear?: any;
	defaultValue?: any;
	Key: any;
	label?: any;
	icon?: any;
	page?: any;
	hideArrow?: boolean;
}) => {
	const [selectedCount, setSelectedCount] = useState<any>(null);
	const [searchTerm, setSearchTerm] = useState<any>("");
	const [allOptions, setAllOptions] = useState<any>(options);
	const [filteredOptions, setFilteredOptions] = useState<any>(options);
	const [checked, setChecked] = useState(true);
	const [checkedList, setCheckedList] = useState<any>(defaultValue || []);
	const { Paragraph, Text } = Typography;
	const [ellipsis, setEllipsis] = useState(true);

	// Update checkedList when defaultValue changes
	useEffect(() => {
		setCheckedList(defaultValue || []);
		setSelectedCount(defaultValue?.length || 0);
	}, [defaultValue]);

	useEffect(() => {
		// Update the filtered options when checkedList changes
		setFilteredOptions(
			options
				?.map((option: any) => ({
					...option,
					checked: checkedList?.includes(option.value),
				}))
				.filter((option: any) =>
					option.label?.toLowerCase().includes(searchTerm.toLowerCase())
				)
		);
		// console.log("Options", options)
		// console.log("Checked List", checkedList)
	}, [options, checkedList, searchTerm]);

	// Search function to search in dropdown
	const handleSearch = (term: string) => {
		setSearchTerm(term);
		const filtered = options.filter((option: any) =>
			option.label.toLowerCase().includes(term.toLowerCase())
		);
		setFilteredOptions(filtered);
	};

	// Set filter values and number of values/checkboxes selected, and store filter values
	const handleCheckboxChange = (selectedValues: string[]) => {
		console.log(selectedValues);
		onChange(selectedValues);
		setCheckedList(selectedValues);
		setSelectedCount(selectedValues.length);
	};

	// Handle specific check box changes rather than the general change
	const handleSpecificCheckboxChange = (e: CheckboxChangeEvent) => {
		let list = checkedList || [];

		let foundIndex = list.findIndex((value: any) => e.target.value === value);
		if (foundIndex === -1) {
			list.push(e.target.value);
		} else {
			list.splice(foundIndex, 1);
		}

		onChange(list);
		setCheckedList(list);
		setSelectedCount(list.length);
	};

	// Reset filter values, number of checkboxes selected, search value of correspoding dropdown
	const handleReset = () => {
		const hasClearHandler = typeof onClear === "function";
		if (hasClearHandler) {
			onClear();
		} else {
			onChange([]);
			setCheckedList([]);
			setSelectedCount(0);
		}

		toggleChecked();
		setChecked(!checked);
		setSearchTerm("");
		setFilteredOptions(options);
	};

	// Render custom dropdown with search column and checkboxes
	const renderDropdown = () => {
		console.log("Rendering dropdown with defaultValue:", defaultValue);
		return (
			<div className="checkbox-dropdown-content">
				<div style={{ padding: "8px", width: "auto", maxWidth: "300px" }}>
					<Input
						className="search-input"
						placeholder="Search"
						value={searchTerm}
						prefix={<SearchOutlined />}
						onChange={(e) => handleSearch(e.target.value)}
						onPressEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
							e.currentTarget.blur();
						}}
						allowClear
					/>
				</div>

				<Checkbox.Group
					style={{
						display: "flex",
						width: "100%",
						maxWidth: "300px",
						justifyItems: "left",
						flexDirection: "column",
						padding: "0 10px 15px",
					}}
					value={checkedList}
					onChange={handleCheckboxChange}
				>
					{filteredOptions?.map((option: any) => (
						<Checkbox
							key={option.value}
							value={option.value}
							className="custom-checkbox"
							title={option.value}
						>
							<Paragraph
								style={ellipsis ? { width: 200 } : undefined}
								ellipsis={ellipsis ? { tooltip: option.label } : false}
							>
								{option.label}
							</Paragraph>
						</Checkbox>
					))}
				</Checkbox.Group>
			</div>
		);
	};

	const toggleChecked = () => {
		setChecked(!checked);
	};

	// Render custom dropdown with reset as footer
	const renderFooter = () => {
		return (
			<div className="checkbox-dropdown-footer">
				<Button
					type="default"
					onClick={handleReset}
					style={{
						border: "none",
						boxShadow: "none",
						background: "transparent",
					}}
				>
					Reset
				</Button>
			</div>
		);
	};

	return (
		<div>
			{page === "timewriting" ? (
				// Render divs for timewriting page
				<Dropdown
					className="checkbox-dropdown"
					dropdownRender={() => (
						<div style={{ width: "100%" }}>
							{renderDropdown()}
							{renderFooter()}
						</div>
					)}
					placement="bottomLeft"
					trigger={["click"]}
				>
					<div>
						<span style={{ display: "flex", alignItems: "center" }}>
							{label}{" "}
							{checkedList?.length > 0 ? `(${checkedList.length})` : null}{" "}
						</span>
					</div>
				</Dropdown>
			) : (
				// Render dropdown and button for other pages
				<Dropdown
					className="checkbox-dropdown"
					dropdownRender={() => (
						<div style={{ width: "100%" }}>
							{renderDropdown()}
							{renderFooter()}
						</div>
					)}
					placement="bottomRight"
					trigger={["click"]}
				>
					<Button
						className="checkbox-dropdown-button"
						style={{
							border: "none",
							boxShadow: "none",
							background: "transparent",
							display: "flex",
							alignItems: "center",
							width: "100%",
						}}
					>
						<div className="flex items-center gap-2 w-full justify-between">
							<span>
								{label}{" "}
								{checkedList?.length > 0 ? `(${checkedList.length})` : null}
							</span>
							{icon && <span>{icon}</span>}
							{!hideArrow && <span>{GetAntIcon("downarrow")}</span>}
						</div>
					</Button>
				</Dropdown>
			)}
		</div>
	);
};

export default CheckboxDropdown;
